import { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, Typography } from "@mui/material";
import moment from "moment";
import { datatypes, inputTypes, selectOptionsKeyName } from "data/globalData";
import {
	poHeaderColumnsObj,
	poHeaderEditableGridColumnList,
	poHeaderChannelDeliveryColumnList,
	poHeaderRightColumnList,
	syncPoHeaderDraft,
	syncPoVendorDefaults,
} from "../data";
import PoGeneralHeaderGrid from "../PoGeneralHeaderGrid";
import { poColors, poPrimaryBtnSx, poSecondaryBtnSx } from "../poUi";

const formatDisplayValue = (value, column, labels) => {
	const named = labels?.[column?.key];
	if (named) return named;
	if (value === undefined || value === null || value === "") return "—";
	if (column?.data_type === datatypes.DATE.key) {
		const m = moment(value);
		return m.isValid() ? m.format("DD/MM/YYYY") : String(value);
	}
	if (column?.key === "entry_mode" || column?.input_type === inputTypes.SELECT.key) {
		const list = column?.[selectOptionsKeyName]?.list || [];
		const match = list.find((opt) => opt.key === value || String(opt.key) === String(value));
		if (match) return match.name;
		if (typeof value === "boolean") return value ? "Yes" : "No";
	}
	return String(value);
};

const DisplayCell = ({ column, value, labels }) => (
	<Box
		sx={{
			minWidth: 0,
			py: 0.45,
			borderBottom: `1px solid ${poColors.line}`,
		}}>
		<Typography sx={{ fontSize: 13, fontWeight: 600, color: poColors.muted, mb: 0.35 }}>{column.name}</Typography>
		<Typography
			sx={{
				fontSize: 14,
				fontWeight: 500,
				color: poColors.ink,
				wordBreak: "break-word",
			}}>
			{formatDisplayValue(value, column, labels)}
		</Typography>
	</Box>
);

const DisplayPoHeaderDetails = ({ data, onSave, disableEdit = false, saving = false, masterLabels = {} }) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [draft, setDraft] = useState(data || {});
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		setDraft(syncPoVendorDefaults(data || {}));
	}, [data]);

	const setDraftSynced = (updater) => {
		setDraft((prev) => {
			const next = typeof updater === "function" ? updater(prev) : updater;
			return syncPoHeaderDraft(prev, next);
		});
	};

	const handleClose = () => {
		if (submitting || saving) return;
		setDraft(syncPoVendorDefaults(data || {}));
		setModalOpen(false);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (submitting || saving) return;
		setSubmitting(true);
		try {
			if (typeof onSave === "function") {
				await onSave(syncPoVendorDefaults(draft));
			}
			setModalOpen(false);
		} finally {
			setSubmitting(false);
		}
	};

	const displayColumns = [...poHeaderEditableGridColumnList, ...poHeaderRightColumnList];

	return (
		<Box width="100%">
			<Box width="100%" display="flex" justifyContent="space-between" alignItems="center" pb={1}>
				<Typography sx={{ fontSize: 14, fontWeight: 700, color: poColors.ink, letterSpacing: "0.01em" }}>
					General
				</Typography>
				{!disableEdit && (
					<Button variant="contained" size="small" onClick={() => setModalOpen(true)} sx={poPrimaryBtnSx}>
						Edit
					</Button>
				)}
			</Box>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: {
						xs: "1fr",
						sm: "1fr 1fr",
						md: "1fr 1fr 1fr",
						lg: "1fr 1fr 1fr 1fr",
					},
					columnGap: { xs: 1.25, md: 1.5 },
					rowGap: 0.35,
				}}>
				{displayColumns.map((column) => (
					<DisplayCell key={column.key} column={column} value={data?.[column.key]} labels={masterLabels} />
				))}
			</Box>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
					columnGap: { xs: 1.25, md: 1.5 },
					maxWidth: { sm: "50%" },
					mt: 0.35,
				}}>
				{poHeaderChannelDeliveryColumnList.map((column) => (
					<DisplayCell key={column.key} column={column} value={data?.[column.key]} labels={masterLabels} />
				))}
			</Box>

			<Box sx={{ mt: 0.75 }}>
				<DisplayCell column={poHeaderColumnsObj.remarks} value={data?.remarks} labels={masterLabels} />
			</Box>

			<Dialog fullWidth maxWidth="md" open={modalOpen} onClose={handleClose} component="form" onSubmit={handleSubmit}>
				<Box width="100%" p={2.25} boxSizing="border-box">
					<Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1.5 }}>Edit general details</Typography>
					<PoGeneralHeaderGrid
						columns={poHeaderEditableGridColumnList}
						rowColumns={poHeaderChannelDeliveryColumnList}
						footerColumns={[poHeaderColumnsObj.remarks]}
						data={draft}
						setData={setDraftSynced}
						masterLabels={masterLabels}
					/>
				</Box>
				<DialogActions sx={{ px: 2.25, pb: 2 }}>
					<Button variant="outlined" onClick={handleClose} disabled={submitting || saving} sx={poSecondaryBtnSx}>
						Cancel
					</Button>
					<Button
						autoFocus
						variant="contained"
						type="submit"
						disabled={submitting || saving}
						sx={poPrimaryBtnSx}>
						{submitting || saving ? "Saving…" : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default DisplayPoHeaderDetails;
